<template>
  <div
    class="vcore_input-group"
    :class="{ 'vcore_has-error': !!error, 'feedback-padding': !error }"
  >
    <label
      v-if="label"
      class="vcore_input-label"
      :class="{ 'vcore_input-label_optional': !required }"
      :style="{ color: labelColor }"
      :for="id | idFormat"
      :id="ariaLabelledBy | ariaFormat"
    >
      {{ label }}
    </label>
    <button v-if="popover" :ref="`${id}_label-icon` | idFormat" class="popover-trigger"
      aria-label="More info" type="button">
      <i class="vcore_icon icon-info"></i>
    </button>
    <Popover v-if="popover && popoverTarget"
             :placement="popover.placement"
             :target="popoverTarget"
             :title="popover.title"
             :content="popover.content"
    ></Popover>
    <datepicker
      :input-class="['vcore_input', { 'vcore_input--error': !!error, [size]: !!size }, ...additionalClasses]"
      @input="handleInput"
      @keyup.native="keyPress"
      @blur="$emit('blur')"
      @closed="$emit('blur')"
      v-model="content"
      :required="required"
      :name="name || label"
      :aria-labelledby="ariaLabelledBy | ariaFormat"
      :aria-invalid="`${!!error}`"
      :aria-required="`${required}`"
      :aria-errormessage="ariaErrorMessage"
      :id="id | idFormat"
      :placeholder="placeholder"
      format="MM/dd/yyyy"
      :calendar-button="false"
      calendar-button-icon="vcore_icon icon-close"
      :typeable="true"
      :disabled="disabled"
      v-bind="datepickerOptions"
    ></datepicker>
    <span class="vcore_input-addon"> </span>
    <div
      class="vcore_input-feedback feedback-position"
      :id="errorId | idFormat"
      v-if="error"
      aria-live="assertive"
    >{{ error }}</div>
    <div
      class="vcore_input-feedback feedback-position"
      :id="hintId | idFormat"
      v-else-if="hint"
      aria-live="assertive"
    >
      {{ hintText }}
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import Popover from "./Popover";
import moment from "moment";
import { resolvePropertyByString } from "../utils/Object";
import { resolveDisabledDatesOptions } from "../utils/Datepicker";

//isMobileDeviceiOS: /iPhone|iPad/i.test(navigator.userAgent), // Using regex to look at user agent to see if device is iPhone or iPad

export default {
  name: "Dateselector",
  props: {
    label: String,
    name: String,
    value: {},
    placeholder: String,
    labelStyle: String,
    inputStyle: String,
    size: {
      default: ""
    },
    labelColor: String,
    required: Boolean,
    error: String,
    hint: Object,
    formName: String,
    popover: Object,
    datepicker: Object,
    additionalClasses: { type: Array, default: () => [] },
    disabled: {type: Boolean, default: false}
  },
  components: {
    Datepicker,
    Popover
  },
  data() {
    return {
      content: this.value || new Date(),
    }
  },
  watch: {
    value() {
      this.content = this.value;
    }
  },
  computed: {
    id() {
      if (this.formName) {
        return `${this.formName}_${this.label}`;
      }
      return this.label || this.name;
    },

    ariaLabelledBy() {
      return `${this.id}--label`;
    },

    ariaErrorMessage() {
      return this.error ? this.$options.filters.idFormat(this.errorId) : undefined;
    },

    errorId() {
      return `${this.id}--error`;
    },

    hintId() {
      return `${this.id}--hint`;
    },

    hintText() {
      if (this.hint.type === "store") {
        return resolvePropertyByString(this.$store.state, this.hint.value);
      } else {
        return this.hint.value;
      }
    },

    datepickerOptions() {
      return resolveDisabledDatesOptions(this.datepicker);
    }
  },
  methods: {
    handleInput() {
      var x = this.content;
      if (typeof x !== 'undefined' && x instanceof Date) {
        var yr = x.getFullYear();
        // "2001" is what gets set for whatever reason by default
        if (yr == '2001') {
          var currentYear = new Date().getFullYear();

          var dateObject1 = new Date(currentYear, x.getMonth(), x.getDate());
          this.$emit('input', dateObject1)
        }
        else {
          var dateObject2 = new Date(yr, x.getMonth(), x.getDate());
          this.$emit('input', dateObject2)
        }
      }
      else {
        this.$emit('input', x); // if x is not a Date
      }      
    },
    keyPress(e){
      /* 
        Manipulating the DOM to auto type slashes.
        This is not best practice, but it is the only way
        to accomplish this with the vuejs-datepicker package
      */
      let input = document.getElementById("statement-payment-due-date")     
      if (e.which !== 8) {
        let out = input.value.replace(/\D/g, '');
        let len = out.length;

        if (len > 1 && len < 4) {
          out = out.substring(0, 2) + '/' + out.substring(2, 3);
        } else if (len >= 4) {
          out = out.substring(0, 2) + '/' + out.substring(2, 4) + '/' + out.substring(4, len);
          out = out.substring(0, 10)
        }
        input.value = out;
      }

      var x = e.which || e.keyCode
      switch(true){
        case x >= 48 && x <= 57: // 0-9
        case x === 45: // dash
        case x === 47: // slash
          return;
        default:
          //console.log(x);
          break;
      }
      e.stopPropagation();
      e.preventDefault();
    }
  },
  mounted() {
    this.content = this.value;
    this.handleInput();
  }
}
</script>

<style lang="scss" scoped>
input.vcore_input {
  &.small {
    height: 30px;
  }

  &.large {
    height: 50px;
  }
}

.vcore_input-group {

  .vcore_input-label + button.popover-trigger {
    display: inline-block;
    color: $color_dark-blue;
    cursor: pointer;
    margin-left: 4px;

    .vcore_icon {
      font-size: 16px;
    }
  }
}

.feedback-position {
  position: absolute;
}

.popover-trigger {
  border-radius: 500px;
  border: none;
  background: none;
  width: fit-content;
  height: fit-content;
  padding: 0;
  margin-left: 4px;
}

.popover-trigger:focus-visible {
  outline: 2px solid black;
  outline-offset: 2px;
}
</style>
